import React, { KeyboardEventHandler } from 'react';

import styled, { useTheme } from 'styled-components';

import { TextFieldProps } from '@mui/material/TextField/TextField';
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from 'components';
import { ControllerProps } from 'react-hook-form/dist/types/controller';

import { Control, FieldError, IMaskInput } from 'third-party';
import { Controller, _, useTranslation } from 'third-party';

import { getLabelColor } from 'utils/order';

const StyledFormControl = styled(FormControl)`
  width: 100%;
`;

const StyledTextField = styled(TextField)<{
  variant?: string;
  hasErrorWhenDisabled?: boolean;
  isSelect?: boolean;
}>`
  width: inherit;
  input,
  textArea {
    &::placeholder {
      opacity: 1;
      color: ${props => props.theme.custom.palette.gray};
    }

    &::-ms-input-placeholder {
      color: ${props => props.theme.custom.palette.gray};
    }
  }

  ${props =>
    props.hasErrorWhenDisabled &&
    props.isSelect &&
    `
    .MuiOutlinedInput-root {
      &.Mui-disabled {
        .MuiOutlinedInput-notchedOutline {
          border-color: ${props.theme.custom.palette.error500} !important;
        }
      }
    }
  `}
`;

export const StyledFormFieldLabel = styled(Grid)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  margin-bottom: 0.8rem;
`;

export const StyledFormFieldTypography = styled(Typography)`
  display: inline;
  min-height: 2.1rem;
`;

export const StyledFormHelperText = styled(FormHelperText)`
  margin: 0 0 0 auto;
  font-size: 1.4rem;
  line-height: 2.1rem;
  font-weight: 400;
  color: ${props => props.theme.custom.palette.error500};
`;

const SelectPlaceholder = styled(Typography)`
  color: ${props => props.theme.custom.palette.gray};
`;

const LoadMoreWrapper = styled.div`
  display: flex;
  padding: 0 1rem 1rem 1rem;
  justify-content: center;
  align-items: center;
`;

export type FormFieldProps = {
  fieldName: string;
  label?: string;
  fieldError?: FieldError; // error object from react-hook-form
  requiredErrorMessage?: string; // custom message
  showError?: boolean;
  isUpdating?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  isLoading?: boolean;
  selectOptions?: React.ReactNode;
  control: Control<any, any>;
  maxLength?: number;
  placeholder?: TextFieldProps['placeholder'];
  defaultValue?: TextFieldProps['defaultValue'];
  rules?: any;
  type?: TextFieldProps['type'];
  size?: TextFieldProps['size'];
  inputProps?: TextFieldProps['inputProps'];
  InputProps?: TextFieldProps['InputProps'];
  showLoadMore?: boolean;
  handleLoadMoreClick?: () => void;
  loadMorePending?: boolean;
  onKeyDownCallback?: () => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onBlur?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  multiline?: boolean;
  showIsRequiredMark?: boolean;
  className?: string;
  rows?: number;
  render?: ControllerProps['render'];
  mask?: any;
  maskDefinitions?: { [key: string]: RegExp };
  maskOptions?: any;
};

interface TextMaskProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  maskdefinitions?: { [key: string]: RegExp };
}

const TextMaskCustom = React.forwardRef<HTMLElement, TextMaskProps>(
  function TextMaskCustom(props, ref) {
    const { onChange, maskdefinitions, ...other } = props;
    return (
      <IMaskInput
        {...other}
        definitions={maskdefinitions}
        // @ts-ignore
        inputRef={ref}
        onAccept={(value: any) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  },
);

export const FormField: React.FC<FormFieldProps> = ({
  showError = true,
  fieldError,
  requiredErrorMessage,
  isUpdating,
  isDisabled,
  fieldName,
  label,
  placeholder,
  isRequired = true,
  showIsRequiredMark = true,
  selectOptions,
  isLoading,
  control,
  defaultValue,
  className,
  type = 'text',
  size = 'small',
  maxLength,
  rules = {},
  inputProps,
  InputProps,
  showLoadMore,
  handleLoadMoreClick,
  loadMorePending,
  onKeyDownCallback,
  multiline,
  rows,
  mask,
  maskDefinitions,
  maskOptions = {},
  render,
  onChange,
  onBlur,
  onKeyUp,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const titleColor = getLabelColor(showError && !!fieldError?.message, theme);

  const tooLongValueError = `${t(
    'order.sectionField.emptyFieldErrorTooLong',
  )} ${maxLength} ${t('order.sectionField.emptyFieldErrorTooLongEnd')}`;

  const selectProps = (field: { value: any }) => {
    let displayValue = '';

    if (isLoading) {
      displayValue = t('common.loading');
    } else {
      if (placeholder && !field.value) {
        displayValue = placeholder;
      }
    }

    return displayValue
      ? {
          displayEmpty: true,
          renderValue: () => (
            <SelectPlaceholder>{displayValue}</SelectPlaceholder>
          ),
        }
      : {};
  };

  const isSelectDisabled = type === 'select' && _.isEmpty(selectOptions);
  const isFieldDisabled =
    isUpdating || isDisabled || isLoading || isSelectDisabled;

  const hasErrorWhenDisabled =
    isFieldDisabled && showError && !!fieldError?.message;
  const isSelectField = type === 'select';

  const handleKeyDownPress: KeyboardEventHandler<HTMLDivElement> = e => {
    if (e.keyCode === 13 && onKeyDownCallback) {
      onKeyDownCallback();
    }
  };

  return (
    <StyledFormControl
      style={{ marginTop: label ? '1.6rem' : '0.4rem' }}
      className={className}
    >
      <StyledFormFieldLabel container>
        {label && (
          <StyledFormFieldTypography variant="subtitle2" color={titleColor}>
            {label}
            {isRequired && showIsRequiredMark ? '*' : ''}
          </StyledFormFieldTypography>
        )}
        {showError && fieldError?.message && (
          <StyledFormHelperText>{fieldError?.message}</StyledFormHelperText>
        )}
      </StyledFormFieldLabel>
      <Controller
        rules={{
          required: {
            value: isRequired,
            message: requiredErrorMessage || '',
          },
          ...(maxLength
            ? {
                maxLength: {
                  value: maxLength,
                  message: tooLongValueError,
                },
              }
            : {}),
          ...rules,
          validate: {
            ...(isRequired
              ? {
                  errorOnSpaces: (value: string) =>
                    !!_.trim(value) || requiredErrorMessage,
                }
              : {}),
            ...(rules?.validate ? rules.validate : {}),
          },
        }}
        control={control}
        name={fieldName}
        render={props =>
          render ? (
            render(props)
          ) : (
            <StyledTextField
              {...props.field}
              size={size}
              type={type}
              multiline={multiline}
              rows={rows}
              defaultValue={defaultValue}
              disabled={isFieldDisabled}
              select={!!selectOptions}
              SelectProps={selectProps(props.field)}
              placeholder={isLoading ? t('common.loading') : placeholder}
              variant="outlined"
              autoComplete="on"
              error={showError && !!fieldError?.message}
              hasErrorWhenDisabled={hasErrorWhenDisabled}
              isSelect={isSelectField}
              // @ts-ignore
              onKeyUp={onKeyUp}
              onChange={event => {
                props.field.onChange(event);
                onChange?.(event);
              }}
              onBlur={onBlur}
              inputProps={{
                ...inputProps,
                mask,
                maskdefinitions: maskDefinitions,
                ...(mask && maskOptions),
                ...(maxLength && { maxLength: maxLength }),
              }}
              onKeyDown={handleKeyDownPress}
              InputProps={{
                ...InputProps,
                ...(mask
                  ? {
                      inputComponent: TextMaskCustom as any,
                    }
                  : {}),
              }}
            >
              {selectOptions}
              {showLoadMore && (
                <LoadMoreWrapper>
                  <Button
                    variant="contained"
                    onClick={handleLoadMoreClick}
                    loading={loadMorePending}
                  >
                    {t('common.loadMore')}
                  </Button>
                </LoadMoreWrapper>
              )}
            </StyledTextField>
          )
        }
      />
    </StyledFormControl>
  );
};
